import { DragDropModule } from '@angular/cdk/drag-drop';
import { APP_BASE_HREF } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { AboutComponent } from './about/about.component';
import { AppRoutingModule } from './app-routing.module';
import { MatTableExporterModule } from 'mat-table-exporter';

/** Components*/
import { AppComponent } from './app.component';
import { AlertComponent } from './common/alert/alert.component';
import { ConfirmModelComponent } from './common/confirm-model/confirm-model.component';
import { Globals } from './models/globals.model';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalService, MSAL_INSTANCE }from '@azure/msal-angular';
import { MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG }from '@azure/msal-angular';
import { MsalGuardConfiguration }from '@azure/msal-angular';
import { MsalInterceptorConfiguration }from '@azure/msal-angular';
import { NavBarComponent } from './nav-bar/nav-bar.component';
import { OverageComponent } from './overage/overage.component';
import { MatChipsModule } from '@angular/material/chips';

/** Services */
import { GlobalService } from './services/global.service';
import { HttpService } from './services/http.service';
import { AddFilterComponent } from './common/add-filter/add-filter.component';
import { TenantComponent } from './tenant/tenant.component';
import { ComponentComponent } from './component/component.component';
import { DatasourceComponent } from './datasource/datasource.component';
import { TenantPageComponent } from './view-page/tenant-page/tenant-page.component';
import { ComponentPageComponent } from './view-page/component-page/component-page.component';
import { DatasourcePageComponent } from './view-page/datasource-page/datasource-page.component';
import { KeyvaultComponent } from './keyvault/keyvault.component';
import { XsrfInterceptor } from './services/xsrf.interceptor';
import { environment } from 'src/environments/environment';
import { NotificationsComponent } from './notifications/notifications.component';
import { NotificationPageComponent } from './view-page/notification-page/notification-page.component';

import { DatePipe } from '@angular/common';
import { TenantDeleteComponent } from './automation/tenant-delete/tenant-delete.component';
import { TenantExportComponent } from './automation/tenant-export/tenant-export.component';

let enValue = environment;
// checks if the app is running on IE
export const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export const protectedResourceMap: [string, string[]][] = [
  [enValue.platformAdminApi_client_resourceUri, [enValue.platformAdminApi_client_resourceScope]],
  [enValue.graphApi_client_resourceUri, [enValue.graphApi_client_resourceScope]]
];

function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: enValue.clientId,
      redirectUri: enValue.redirectUri,
      authority: enValue.authority,
      postLogoutRedirectUri: enValue.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: <BrowserCacheLocation>enValue.cache_client.cacheLocation,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    }
  });
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(enValue.platformAdminApi_client_resourceUri, [enValue.platformAdminApi_client_resourceScope]);
  protectedResourceMap.set(enValue.graphApi_client_resourceUri, [enValue.graphApi_client_resourceScope]);

  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  };
}

@NgModule({ declarations: [
        AppComponent,
        NavBarComponent,
        OverageComponent,
        AboutComponent,
        AlertComponent,
        ConfirmModelComponent,
        AddFilterComponent,
        TenantComponent,
        ComponentComponent,
        DatasourceComponent,
        TenantPageComponent,
        ComponentPageComponent,
        DatasourcePageComponent,
        KeyvaultComponent,
        NotificationsComponent,
        NotificationPageComponent,
        TenantDeleteComponent,
        TenantExportComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    bootstrap: [AppComponent], imports: [BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        MatStepperModule,
        MatToolbarModule,
        MatIconModule,
        MatTabsModule,
        MatSidenavModule,
        MatMenuModule,
        MatFormFieldModule,
        MatSelectModule,
        MatListModule,
        MatTooltipModule,
        MatTableModule,
        MatDialogModule,
        MatInputModule,
        MatSortModule,
        MatPaginatorModule,
        MatRadioModule,
        MatAutocompleteModule,
        MatChipsModule,
        BrowserAnimationsModule,
        NgbModule,
        NgMultiSelectDropDownModule.forRoot(),
        AppRoutingModule,
        TypeaheadModule.forRoot(),
        MatDatepickerModule,
        MatNativeDateModule,
        MatRippleModule,
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        MatCheckboxModule,
        DragDropModule,
        MatTableExporterModule,
        MatExpansionModule], providers: [
        {
            provide: APP_INITIALIZER,
            useValue: () => new Promise(resolve => setTimeout(resolve, 2000)),
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: XsrfInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useValue: {
                interactionType: InteractionType.Redirect
            } as MsalGuardConfiguration
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        DatePipe,
        Globals,
        { provide: APP_BASE_HREF, useValue: '/' }, HttpService, GlobalService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {

  constructor(public globalService: GlobalService, public globals: Globals, public http: HttpClient) {

    // global config details
    this.globals.config = enValue;

    this.globals.releaseDetails = enValue.ReleaseDetails;

    // Check Subscription
    this.globals.subscription = enValue.subscription;
    this.globalService.environment = enValue.environment;

    // Check Tag Movement Environment
    //this.globalService.tagMovMsg = enValue.moveTagMasterMsg;
  }
}
